import find from 'lodash/find'
import { AnyAction } from '@reduxjs/toolkit'

import type { FetchStatusCommon } from 'restApi/types'

type StoryOffer = 'complimentary' | 'discounted_price' | 'no_discount'

export type MyListingData = {
  id: number
  title: string
  url: string
  status: string
  may_submit: boolean
  story_offer: {
    weekend_offer: StoryOffer
    weekday_offer: StoryOffer
    weekend_discounted_price: string
    weekday_discounted_price: string
    additional_info: string
  }
  compliance_number: string
  compliance_number_exempt: boolean
  compliance_number_required: boolean
  on_airbnb: boolean
  story_eligible: boolean
  tax_rates: Array<R_TaxRate>
  tax_exclusive: boolean
  export_calendar_url: string
  calendar_export_confirmed: boolean
  channel_manager?: 'siteminder' | 'little_hotelier'
  channel_manager_sync_enabled?: boolean
  promo_image: {
    medium: string
  }
  listing_type: {
    name: string
  }
  price: number
  cleaning_fee: number
  child_price: number
  weekend_price: number
  weekend_price_includes_sunday: number
  extra_person_fee: number
  extra_child_fee: number
  extra_person_threshold: number
  pet_friendly: boolean
  pet_fee: number
  pet_fee_type: string
  max_pets: number
  security_deposit: number
  max_security_deposit: number
  long_stay_discounts: Array<{ percentage: number; duration_in_nights: number }>
  pricing_suffix: string
  nice_pricing_suffix: string
  service_fee_included: boolean
  host_service_fee_base_rate: string
  customer_service_fee_base_rate: string
  market: R_Market
  booking_window: number
}

type MyListingState = FetchStatusCommon & {
  // many of these properties are returned by the rest api, and not typed yet
  data: MyListingData
  creating: boolean
  photos: any[] | null
  uploading: boolean
  redirectAfterFormSubmit: any | null
}

const initialState: MyListingState = {
  data: null,
  photos: null,
  updated: false,
  fetching: false,
  fetched: false,
  submitting: false,
  creating: false,
  updating: false,
  uploading: false,
  error: null,
  redirectAfterFormSubmit: null,
}

export default function reducer(
  state = initialState,
  action: AnyAction,
): MyListingState {
  switch (action.type) {
    case 'MY_LISTING_RESET':
      return initialState

    case 'MY_LISTING_FETCH_PENDING':
      return {
        ...state,
        fetching: true,
      }

    case 'MY_LISTING_FETCH_REJECTED':
      return {
        ...state,
        fetching: false,
        error: action.payload,
      }

    case 'MY_LISTING_FETCH_FULFILLED':
      return {
        ...state,
        fetching: false,
        fetched: true,
        data: action.payload.data,
      }

    case 'MY_LISTING_SUBMIT_FOR_REVIEW_PENDING':
      return {
        ...state,
        updating: true,
        submitting: true,
      }

    case 'MY_LISTING_SUBMIT_FOR_REVIEW_REJECTED':
      return {
        ...state,
        updating: false,
        submitting: false,
        error: action.payload,
      }

    case 'MY_LISTING_SUBMIT_FOR_REVIEW_FULFILLED':
      // Submissions can come from the listings index cards too — only
      // refresh data when it belongs to the loaded listing
      return {
        ...state,
        submitting: false,
        updating: false,
        data:
          state.data?.id === action.payload.data?.id
            ? action.payload.data
            : state.data,
      }

    case 'MY_LISTING_MAY_SUBMIT_UPDATED':
      if (!state.data) return state
      return {
        ...state,
        data: { ...state.data, may_submit: action.payload },
      }

    case 'MY_LISTING_CREATE_DRAFT_PENDING':
      return {
        ...state,
        creating: true,
      }

    case 'MY_LISTING_CREATE_DRAFT_REJECTED':
      return {
        ...state,
        creating: false,
        error: action.payload,
      }

    case 'MY_LISTING_CREATE_DRAFT_FULFILLED':
      return {
        ...state,
        creating: false,
      }

    case 'MY_LISTING_UPDATE_PENDING':
      return {
        ...state,
        updating: true,
      }

    case 'MY_LISTING_UPDATE_REJECTED':
      return {
        ...state,
        updating: false,
        error: action.payload,
      }

    case 'MY_LISTING_UPDATE_FULFILLED':
      return {
        ...state,
        data: action.payload.data,
        updating: false,
      }

    case 'MY_LISTING_PHOTOS_FETCH_PENDING':
      return {
        ...state,
        fetching: true,
      }

    case 'MY_LISTING_PHOTOS_FETCH_REJECTED':
      return {
        ...state,
        fetching: false,
        error: action.payload,
      }

    case 'MY_LISTING_PHOTOS_FETCH_FULFILLED':
      return {
        ...state,
        photos: action.payload.data,
        fetching: false,
      }

    case 'MY_LISTING_PHOTOS_CREATE_PENDING':
      return {
        ...state,
        uploading: true,
      }

    case 'MY_LISTING_PHOTOS_CREATE_REJECTED':
      return {
        ...state,
        uploading: false,
        error: action.payload,
      }

    case 'MY_LISTING_PHOTOS_CREATE_FULFILLED':
      return {
        ...state,
        uploading: false,
        photos: action.payload.data,
        data: {
          ...state.data,
          promo_image: find(action.payload.data, (image) => image.default)
            .image,
        },
      }

    case 'MY_LISTING_PHOTOS_SET_DEFAULT_PENDING':
      return {
        ...state,
        uploading: true,
      }

    case 'MY_LISTING_PHOTOS_SET_DEFAULT_REJECTED':
      return {
        ...state,
        uploading: false,
        error: action.payload,
      }

    case 'MY_LISTING_PHOTOS_SET_DEFAULT_FULFILLED':
      return {
        ...state,
        uploading: false,
        photos: action.payload.data,
        data: {
          ...state.data,
          promo_image: find(action.payload.data, (image) => image.default)
            .image,
        },
      }

    case 'MY_LISTING_PHOTOS_DELETE_PENDING':
      return {
        ...state,
        uploading: true,
      }

    case 'MY_LISTING_PHOTOS_DELETE_REJECTED':
      return {
        ...state,
        uploading: false,
        error: action.payload,
      }

    case 'MY_LISTING_PHOTOS_DELETE_FULFILLED':
      return {
        ...state,
        uploading: false,
        photos: action.payload.data,
      }

    case 'MY_LISTING_PHOTOS_ORDER_PENDING':
      return {
        ...state,
        uploading: true,
      }

    case 'MY_LISTING_PHOTOS_ORDER_FULFILLED':
      return {
        ...state,
        uploading: false,
        photos: action.payload.data,
        data: {
          ...state.data,
          promo_image: find(action.payload.data, (image) => image.default)
            .image,
        },
      }

    case 'MY_LISTING_SET_REDIRECT':
      return {
        ...state,
        redirectAfterFormSubmit: action.payload,
      }

    case 'MY_LISTING_RESET_REDIRECT':
      return {
        ...state,
        redirectAfterFormSubmit: null,
      }

    default:
      return state
  }
}
